import React, { useState } from "react";
import { HiOutlineHome, HiOutlineFolder, HiChevronLeft, HiChevronRight, HiOutlineLogout, HiOutlineKey, HiX } from "react-icons/hi";
import { HiOutlineCollection, HiOutlineDatabase, HiOutlineTerminal, HiOutlineSparkles } from "react-icons/hi";
import { useUser, APIKeys } from "@clerk/clerk-react";
import { Page } from "../App";

const ChevronLeft = HiChevronLeft as React.ComponentType<{ size?: number }>;
const ChevronRight = HiChevronRight as React.ComponentType<{ size?: number }>;
const LogoutIcon = HiOutlineLogout as React.ComponentType<{ size?: number }>;
const ApiKeysIcon = HiOutlineKey as React.ComponentType<{ size?: number }>;
const CloseIcon = HiX as React.ComponentType<{ size?: number }>;

interface SidebarProps {
  activePage: Page;
  onNavigate: (page: Page) => void;
  onSignOut: () => void;
}

type NavIcon = React.ComponentType<{ size?: number }>;

const NAV_ITEMS: { id: Page; label: string; Icon: NavIcon }[] = [
  { id: "files",       label: "Files",              Icon: HiOutlineFolder          as NavIcon },
  { id: "documents",   label: "Search Docs List",   Icon: HiOutlineCollection      as NavIcon },
  { id: "home",        label: "Full Text Search",   Icon: HiOutlineHome            as NavIcon },
  { id: "corpora",     label: "Corpora",            Icon: HiOutlineDatabase        as NavIcon },
  { id: "skills",      label: "Skills",             Icon: HiOutlineSparkles        as NavIcon },
  { id: "api",         label: "API Explorer",       Icon: HiOutlineTerminal        as NavIcon },
];

/**
 * Dashboard sidebar — dark "terminal panel" from the kurnal paper-terminal
 * design (mockup-5): term background, mono labels, amber active accent.
 */
const Sidebar: React.FC<SidebarProps> = ({ activePage, onNavigate, onSignOut }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const { user } = useUser();

  return (
    <aside
      className={`${
        collapsed ? "w-14" : "w-56"
      } bg-kterm text-ktermtext flex flex-col h-full shrink-0 transition-all duration-200 overflow-hidden border-r border-ktermline`}
    >
      {/* Logo — always visible, text hidden when collapsed */}
      <div className="border-b border-ktermline flex items-center px-3 h-[78px] shrink-0">
        {collapsed ? (
          <span className="cursor-block shrink-0" />
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium leading-tight whitespace-nowrap text-white">
              <span className="cursor-block shrink-0" />
              <span>kurnal<span className="text-ktermdim">.ai</span></span>
            </div>
            <div className="text-[11px] font-mono text-ktermdim leading-tight whitespace-nowrap">indexing console</div>
            {/* Tenant = the user's private corpus id (their Clerk user id) */}
            {user && (
              <div
                className="text-[10px] font-mono text-ktermdim/70 leading-tight truncate mt-0.5"
                title={`Tenant: ${user.id}`}
              >
                {user.id}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2.5 py-4 space-y-1 flex flex-col">
        {NAV_ITEMS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => onNavigate(id)}
            title={collapsed ? label : undefined}
            className={`w-full text-left flex items-center gap-3 px-2 py-2 rounded-md font-mono text-[13px] transition-colors ${
              activePage === id
                ? "bg-white/5 text-kamber"
                : "text-ktermdim hover:bg-white/5 hover:text-ktermtext"
            }`}
          >
            <span className="shrink-0"><Icon size={17} /></span>
            {!collapsed && <span className="truncate">{label}</span>}
          </button>
        ))}

        {/* Collapse / expand toggle — pinned to bottom of nav */}
        <div className="flex-1" />
        <button
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-md font-mono text-[13px] text-ktermdim hover:bg-white/5 hover:text-ktermtext transition-colors"
        >
          <span className="shrink-0">
            {collapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </span>
          {!collapsed && <span className="truncate">Collapse</span>}
        </button>
      </nav>

      {/* User / sign-out */}
      <div className="border-t border-ktermline px-2.5 py-3">
        {!collapsed && user && (
          <div className="mb-2 min-w-0 px-2">
            <div className="text-xs font-medium text-ktermtext truncate">
              {user.fullName ?? user.primaryEmailAddress?.emailAddress ?? "Signed in"}
            </div>
            {user.primaryEmailAddress && (
              <div className="text-[11px] font-mono text-ktermdim truncate">{user.primaryEmailAddress.emailAddress}</div>
            )}
          </div>
        )}
        <button
          onClick={() => setShowApiKeys(true)}
          title={collapsed ? "API Keys" : undefined}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-md font-mono text-[13px] text-ktermdim hover:bg-white/5 hover:text-ktermtext transition-colors"
        >
          <span className="shrink-0"><ApiKeysIcon size={17} /></span>
          {!collapsed && <span className="truncate">API Keys</span>}
        </button>
        <button
          onClick={onSignOut}
          title={collapsed ? "Sign out" : undefined}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-md font-mono text-[13px] text-ktermdim hover:bg-white/5 hover:text-ktermtext transition-colors"
        >
          <span className="shrink-0"><LogoutIcon size={17} /></span>
          {!collapsed && <span className="truncate">Sign out</span>}
        </button>
      </div>

      {/* Footer */}
      {!collapsed && (
        <div className="px-4 py-3 border-t border-ktermline text-[11px] font-mono text-ktermdim whitespace-nowrap">
          indexing-worker :3001
        </div>
      )}

      {/* API Keys modal */}
      {showApiKeys && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-kterm/70 p-4"
          onClick={() => setShowApiKeys(false)}
        >
          <div
            className="bg-white rounded-xl border border-krule shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowApiKeys(false)}
              title="Close"
              className="absolute top-3 right-3 text-kfaint hover:text-ink z-10"
            >
              <CloseIcon size={20} />
            </button>
            {/* pr-10 clears the absolute close button so Clerk's "Add new key" isn't covered */}
            <div className="p-4 pr-10">
              <APIKeys />
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
